import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  asBotIdentity,
  lookupBotIdentity,
  type BotIdentity,
  type BotIdentityRow,
} from "./botIdentity";
import {
  FALLBACK_WORKFLOWS,
  type WorkflowBot,
  type WorkflowBotRole,
  type WorkflowNode,
  type WorkflowNodeType,
  type WorkflowRun,
  type WorkflowTemplate,
} from "./workflowFallback";

export type WorkflowRow = {
  id?: number | string | null;
  legend_token?: string | null;
  line_key?: string | null;
  key?: string | null;
  name?: string | null;
  version?: number | null;
  is_default?: boolean | null;
  description?: string | null;
  active?: boolean | null;
};

export type WorkflowNodeRow = {
  id?: number | string | null;
  workflow_id?: number | string | null;
  node_key?: string | null;
  label?: string | null;
  sort_order?: number | null;
  node_type?: string | null;
  is_blind_review?: boolean | null;
};

export type WorkflowBotRow = {
  node_id?: number | string | null;
  agent_id?: string | null;
  role_name?: string | null;
  bot_role?: string | null;
  required?: boolean | null;
  sort_order?: number | null;
  active?: boolean | null;
};

export type WorkflowRunRow = {
  id?: number | string | null;
  workflow_id?: number | string | null;
  legend_token?: string | null;
  current_node_key?: string | null;
  title?: string | null;
  status?: string | null;
  /** Present on a payload must not be stored. Runs do not point at day items. */
  day_item_id?: number | string | null;
};

const NODE_TYPES = new Set<WorkflowNodeType>([
  "produce",
  "review",
  "release",
  "comms",
  "support",
  "archive",
]);

const BOT_ROLES = new Set<WorkflowBotRole>([
  "dri",
  "blind_review",
  "tool",
  "master",
  "support",
]);

function asId(value: number | string | null | undefined) {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value)) return Number(value);
  return undefined;
}

function asNodeType(value: string | null | undefined): WorkflowNodeType {
  return value && NODE_TYPES.has(value as WorkflowNodeType)
    ? (value as WorkflowNodeType)
    : "support";
}

function asBotRole(value: string | null | undefined): WorkflowBotRole {
  return value && BOT_ROLES.has(value as WorkflowBotRole)
    ? (value as WorkflowBotRole)
    : "support";
}

export function assembleWorkflows(
  workflows: WorkflowRow[],
  nodeRows: WorkflowNodeRow[],
  botRows: WorkflowBotRow[],
  identityRows: readonly BotIdentity[] | null = null,
): WorkflowTemplate[] {
  const botsByNode = new Map<number, WorkflowBot[]>();
  for (const row of botRows) {
    if (row.active === false) continue;
    const nodeId = asId(row.node_id);
    const agentId = row.agent_id?.trim();
    const roleName = row.role_name?.trim();
    if (nodeId == null || !agentId || !roleName) continue;
    const list = botsByNode.get(nodeId) ?? [];
    list.push({
      agent_id: agentId,
      role_name: roleName,
      bot_role: asBotRole(row.bot_role),
      required: row.required !== false,
      sort_order: Number(row.sort_order ?? 0),
      active: true,
      identity: lookupBotIdentity(agentId, identityRows),
    });
    botsByNode.set(nodeId, list);
  }
  for (const list of botsByNode.values()) {
    list.sort((a, b) => a.sort_order - b.sort_order);
  }

  const nodesByWorkflow = new Map<number, WorkflowNode[]>();
  for (const row of nodeRows) {
    const workflowId = asId(row.workflow_id);
    const nodeKey = row.node_key?.trim();
    const label = row.label?.trim();
    if (workflowId == null || !nodeKey || !label) continue;
    const nodeId = asId(row.id);
    const list = nodesByWorkflow.get(workflowId) ?? [];
    list.push({
      ...(nodeId != null ? { id: nodeId } : {}),
      node_key: nodeKey,
      label,
      sort_order: Number(row.sort_order ?? 0),
      node_type: asNodeType(row.node_type),
      is_blind_review: row.is_blind_review === true,
      bots: nodeId != null ? (botsByNode.get(nodeId) ?? []) : [],
    });
    nodesByWorkflow.set(workflowId, list);
  }
  for (const list of nodesByWorkflow.values()) {
    list.sort((a, b) => a.sort_order - b.sort_order);
  }

  return workflows.flatMap((row) => {
    if (row.active === false) return [];
    const id = asId(row.id);
    const token = row.legend_token?.trim();
    const key = row.key?.trim();
    const name = row.name?.trim();
    const lineKey = row.line_key?.trim();
    if (id == null || !token || !key || !name || !lineKey) return [];
    return [
      {
        id,
        legend_token: token,
        line_key: lineKey,
        key,
        name,
        version: Number(row.version ?? 1),
        is_default: row.is_default === true,
        description: row.description?.trim() || null,
        nodes: nodesByWorkflow.get(id) ?? [],
        source: "db" as const,
      },
    ];
  });
}

export function indexRuns(rows: WorkflowRunRow[]): Record<number, WorkflowRun> {
  const runs: Record<number, WorkflowRun> = {};
  for (const row of rows) {
    const id = asId(row.id);
    const workflowId = asId(row.workflow_id);
    const token = row.legend_token?.trim();
    const nodeKey = row.current_node_key?.trim();
    if (id == null || workflowId == null || !token || !nodeKey) continue;
    runs[id] = {
      id,
      workflow_id: workflowId,
      legend_token: token,
      current_node_key: nodeKey,
      title: row.title?.trim() || "",
      status: row.status?.trim() || "active",
    };
  }
  return runs;
}

export function pickTemplate(
  templates: WorkflowTemplate[],
  token: string,
): WorkflowTemplate | null {
  const rows = templates.filter((item) => item.legend_token === token);
  if (!rows.length) return null;
  return rows.reduce((best, item) => {
    if (item.is_default && !best.is_default) return item;
    if (item.is_default === best.is_default && item.version > best.version) {
      return item;
    }
    return best;
  });
}

export function templateForToken(
  token: string,
  templates: WorkflowTemplate[],
  source: WorkflowTemplate["source"],
): WorkflowTemplate | null {
  return (
    pickTemplate(templates, token) ??
    (source === "db" ? pickTemplate(FALLBACK_WORKFLOWS, token) : null)
  );
}

export function currentNodeText(label: string | null | undefined) {
  const text = label?.trim();
  return text ? `当前节点：${text}` : null;
}

export function resolveCurrentNodeText(
  runId: number | undefined,
  runs: Record<number, WorkflowRun>,
  templates: WorkflowTemplate[],
): string | null {
  if (runId == null) return null;
  const run = runs[runId];
  if (!run) return null;
  const template =
    templates.find((item) => item.id != null && item.id === run.workflow_id) ??
    pickTemplate(templates, run.legend_token) ??
    pickTemplate(FALLBACK_WORKFLOWS, run.legend_token);
  const node = template?.nodes.find(
    (item) => item.node_key === run.current_node_key,
  );
  return currentNodeText(node?.label ?? run.current_node_key);
}

export function canLoadWorkflowsFromSupabase() {
  if (import.meta.env.MODE === "test") return false;
  return Boolean(
    import.meta.env.VITE_SUPABASE_URL &&
    import.meta.env.VITE_SB_PUBLISHABLE_KEY,
  );
}

export async function loadWorkflowBundle(): Promise<{
  templates: WorkflowTemplate[];
  runs: Record<number, WorkflowRun>;
} | null> {
  if (!canLoadWorkflowsFromSupabase()) return null;
  try {
    const client = getSupabaseClient();
    const [workflows, nodes, bots, runs, identities] = await Promise.all([
      client.from("board_workflows").select("*").eq("active", true),
      client
        .from("board_workflow_nodes")
        .select("*")
        .order("sort_order", { ascending: true }),
      client
        .from("board_workflow_node_bots")
        .select("*")
        .eq("active", true)
        .order("sort_order", { ascending: true }),
      client.from("board_workflow_runs").select("*"),
      client.from("board_bot_identities").select("*"),
    ]);
    if (workflows.error || nodes.error || bots.error) return null;
    const identityStore = identities.error
      ? null
      : ((identities.data ?? []) as BotIdentityRow[]).flatMap((row) => {
          const identity = asBotIdentity(row);
          return identity ? [identity] : [];
        });
    const templates = assembleWorkflows(
      (workflows.data ?? []) as WorkflowRow[],
      (nodes.data ?? []) as WorkflowNodeRow[],
      (bots.data ?? []) as WorkflowBotRow[],
      identityStore,
    );
    if (!templates.length) return null;
    return {
      templates,
      runs: runs.error ? {} : indexRuns((runs.data ?? []) as WorkflowRunRow[]),
    };
  } catch {
    return null;
  }
}
